import { Dispatch } from 'redux';

import * as api from '../api/contentModerationApi';
import { ContentModerationApiError } from '../api/envelope';
import { isDisagreementReasonRequired } from '../helpers/isDisagreementReasonRequired';
import {
  DecisionBody,
  ErrorEntry,
  QueueFilters,
} from '../models';
import * as actions from './contentModerationActions';
import { Action, ContentModerationState } from './contentModerationTypes';

export type ThunkDispatch = Dispatch<Action>;

function toErrorEntry(error: unknown): ErrorEntry {
  if (error instanceof ContentModerationApiError) {
    return { error: error.code, param: error.param };
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return { error: 'network.aborted', param: '' };
  }
  return { error: 'network.error', param: '' };
}

export const fetchQueueThunk =
  (filters: QueueFilters, opts: { isPoll?: boolean } = {}) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    const isPoll = Boolean(opts.isPoll);
    dispatch(actions.queueFetchRequested(isPoll));
    try {
      const { list, totalCount, page, pageSize } = await api.fetchQueue(filters);
      dispatch(actions.queueFetchSuccess(list, totalCount, page, pageSize, Date.now()));
    } catch (error) {
      dispatch(actions.queueFetchFailure(toErrorEntry(error)));
    }
  };

// Load-more variant: fetches the next page using the caller-supplied filters
// (which already have page set to currentPage + 1) and APPENDS the rows to
// the existing list. Caller is the hook, which knows the current page.
export const loadMoreQueueThunk =
  (filters: QueueFilters) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    dispatch(actions.queueAppendRequested());
    try {
      const { list, totalCount, page, pageSize } = await api.fetchQueue(filters);
      dispatch(actions.queueAppendSuccess(list, totalCount, page, pageSize));
    } catch (error) {
      dispatch(actions.queueAppendFailure(toErrorEntry(error)));
    }
  };

export const fetchDetailThunk =
  (id: number) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    dispatch(actions.detailFetchRequested());
    try {
      const detail = await api.fetchEventDetail(id);
      dispatch(actions.detailFetchSuccess(detail));
    } catch (error) {
      dispatch(actions.detailFetchFailure(toErrorEntry(error)));
    }
  };

export const fetchAccountClusterThunk =
  (accountId: number) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    dispatch(actions.clusterFetchRequested(accountId));
    try {
      const cluster = await api.fetchAccountCluster(accountId);
      dispatch(actions.clusterFetchSuccess(cluster));
    } catch (error) {
      dispatch(actions.clusterFetchFailure(toErrorEntry(error)));
    }
  };

export const fetchBodyHashClusterThunk =
  (eventId: number) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    dispatch(actions.bodyHashFetchRequested(eventId));
    try {
      const items = await api.fetchBodyHashCluster(eventId);
      dispatch(actions.bodyHashFetchSuccess(items));
    } catch (error) {
      dispatch(actions.bodyHashFetchFailure(toErrorEntry(error)));
    }
  };

export interface AcquireLockOutcome {
  acquired: boolean;
  outcome: 'Held' | 'HeldByOther' | 'Failure';
}

export const acquireLockThunk =
  (id: number) =>
  async (dispatch: ThunkDispatch): Promise<AcquireLockOutcome> => {
    dispatch(actions.lockAcquireRequested());
    try {
      const result = await api.acquireLock(id);
      const holder =
        result.holderReviewerAccountId != null
          ? { reviewerAccountId: result.holderReviewerAccountId, reviewerName: null }
          : null;
      if (result.outcome === 'heldByAnotherReviewer') {
        dispatch(actions.lockAcquireHeldByOther(holder, result.lastHeartbeatAt));
        return { acquired: false, outcome: 'HeldByOther' };
      }
      dispatch(actions.lockAcquireHeld(holder, result.lastHeartbeatAt));
      return { acquired: true, outcome: 'Held' };
    } catch (error) {
      dispatch(actions.lockAcquireFailure(toErrorEntry(error)));
      return { acquired: false, outcome: 'Failure' };
    }
  };

export const heartbeatLockThunk =
  (id: number) =>
  async (dispatch: ThunkDispatch): Promise<{ ok: boolean; code?: string }> => {
    const result = await api.heartbeatLock(id);
    if (result.ok) {
      dispatch(actions.lockHeartbeatSuccess(new Date().toISOString()));
      return { ok: true };
    }
    dispatch(actions.lockHeartbeatFailure({ error: result.code, param: 'reviewLock' }));
    return { ok: false, code: result.code };
  };

export const releaseLockThunk =
  (id: number) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    dispatch(actions.lockRelease());
    await api.releaseLock(id);
  };

export interface OpenEventResult {
  opened: boolean;
  reason?: 'heldByOther' | 'lockFailure';
}

export const openEventForReviewThunk =
  (id: number) =>
  async (dispatch: ThunkDispatch): Promise<OpenEventResult> => {
    const lockOutcome = await acquireLockThunk(id)(dispatch);
    if (lockOutcome.outcome === 'HeldByOther') {
      return { opened: false, reason: 'heldByOther' };
    }
    if (lockOutcome.outcome === 'Failure') {
      return { opened: false, reason: 'lockFailure' };
    }
    const [detailRes] = await Promise.allSettled([
      fetchDetailThunk(id)(dispatch),
      fetchBodyHashClusterThunk(id)(dispatch),
    ]);
    void detailRes;
    return { opened: true };
  };

export const closeEventForReviewThunk =
  (id: number) =>
  async (dispatch: ThunkDispatch): Promise<void> => {
    await releaseLockThunk(id)(dispatch);
    dispatch(actions.detailClear());
  };

export const submitDecisionThunk =
  (id: number, body: DecisionBody) =>
  async (dispatch: ThunkDispatch): Promise<{ ok: boolean; error?: ErrorEntry }> => {
    dispatch(actions.decisionSubmitRequested(body));
    try {
      const result = await api.submitDecision(id, body);
      dispatch(actions.decisionSubmitSuccess(result.decision, result.warnings));
      return { ok: true };
    } catch (error) {
      const entry = toErrorEntry(error);
      dispatch(actions.decisionSubmitFailure(entry));
      return { ok: false, error: entry };
    }
  };

export interface SubmitDecisionInput {
  eventId: number;
  body: DecisionBody;
  aiScore: number;
}

export const submitDecisionWithReasonThunk =
  (input: SubmitDecisionInput) =>
  async (dispatch: ThunkDispatch): Promise<{ ok: boolean; error?: ErrorEntry }> => {
    const required = isDisagreementReasonRequired(input.aiScore, input.body.decision);
    if (required && !input.body.disagreementReason) {
      const err: ErrorEntry = {
        error: 'disagreementReason.required',
        param: 'disagreementReason',
      };
      dispatch(actions.decisionSubmitRequested(input.body));
      dispatch(actions.decisionSubmitFailure(err));
      return { ok: false, error: err };
    }
    return submitDecisionThunk(input.eventId, input.body)(dispatch);
  };

export const resetDecisionThunk =
  (decisionId: number, reason: string) =>
  async (dispatch: ThunkDispatch): Promise<{ ok: boolean; error?: ErrorEntry }> => {
    dispatch(actions.decisionResetRequested());
    try {
      await api.resetDecision(decisionId, reason);
      dispatch(actions.decisionResetSuccess());
      return { ok: true };
    } catch (error) {
      const entry = toErrorEntry(error);
      dispatch(actions.decisionResetFailure(entry));
      return { ok: false, error: entry };
    }
  };

export interface FanOutSummary {
  total: number;
  succeeded: number;
  failed: number;
}

export const blockEventsFanOutThunk =
  (eventIds: number[], decisionBody: DecisionBody) =>
  async (dispatch: ThunkDispatch): Promise<FanOutSummary> => {
    dispatch(actions.fanOutStart(eventIds));
    let succeeded = 0;
    let failed = 0;
    await Promise.allSettled(
      eventIds.map(async (eventId) => {
        try {
          await api.submitDecision(eventId, decisionBody);
          succeeded += 1;
          dispatch(actions.fanOutEventResult({ eventId, status: 'success' }));
        } catch (error) {
          failed += 1;
          const entry = toErrorEntry(error);
          dispatch(
            actions.fanOutEventResult({
              eventId,
              status: 'failure',
              errorCode: entry.error,
              errorParam: entry.param,
            }),
          );
        }
      }),
    );
    dispatch(actions.fanOutFinish());
    return { total: eventIds.length, succeeded, failed };
  };

// Re-export the state shape for thunk consumers that need to call getState.
export type { ContentModerationState };
